"use client";

import * as React from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@baluu/ui/components/popover";
import type {
  CheckoutChip,
  CheckoutConstraint,
} from "./ticket-conditions-chips";

type TicketConditionsPopoverProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
  chips: CheckoutChip[];
  constraints: CheckoutConstraint[];
  policy: { name: string; summary: string; href?: string } | null;
};

export function TicketConditionsPopover({
  open,
  onOpenChange,
  trigger,
  chips,
  constraints,
  policy,
}: TicketConditionsPopoverProps) {
  const hasContent = chips.length > 0 || constraints.length > 0 || !!policy;
  if (!hasContent) return <>{trigger}</>;

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 space-y-3">
        {constraints.length > 0 && (
          <section>
            <p className="text-xs font-semibold mb-1">Booking rules</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {constraints.map((c, i) => (
                <li key={i}>• {c.summary}</li>
              ))}
            </ul>
          </section>
        )}

        {chips.length > 0 && (
          <section>
            <p className="text-xs font-semibold mb-1">Pricing</p>
            <ul className="space-y-1 text-xs text-muted-foreground">
              {chips.map((c, i) => (
                <li key={i}>• {c.label}</li>
              ))}
            </ul>
          </section>
        )}

        {policy && (
          <section>
            <p className="text-xs font-semibold mb-1">
              {policy.name || "Cancellation policy"}
            </p>
            {policy.summary && (
              <p className="text-xs text-muted-foreground">{policy.summary}</p>
            )}
            {policy.href && (
              <a
                className="mt-1 inline-block text-xs underline text-foreground"
                href={policy.href}
                target="_blank"
                rel="noreferrer"
              >
                Read the full policy
              </a>
            )}
          </section>
        )}
      </PopoverContent>
    </Popover>
  );
}
